import { Container, Eyebrow, Heading, Icon, Reveal, Section, Text } from '@nexa/ui';
import { APP_URL, DESKTOP_DOWNLOADS } from '@/core/config/site.ts';
import { useCopy } from '@/core/i18n';
import { DesktopMock } from './DesktopMock.tsx';
import { DownloadButton } from './DownloadButton.tsx';
import type { DesktopPlatform } from './detectPlatform.ts';
import { useDesktopPlatform } from './useDesktopPlatform.ts';

const PLATFORMS: readonly DesktopPlatform[] = ['mac', 'windows'];

/**
 * `#download` — tải ứng dụng Desktop. KHÔNG có trong bản thiết kế; user chốt 2026-09-28 thêm một
 * section riêng giữa `#faq` và `#cta`, bố cục chữ trái / hình phải như `#collab`.
 *
 * Thứ tự hai nút KHÔNG đổi theo máy người xem — chỉ độ nhấn đổi. Đảo vị trí sau hydrate là làm hai
 * nút nhảy chỗ dưới con trỏ; đổi nền đặc/xám thì cùng kích thước nên không xê dịch pixel nào.
 * Chưa biết máy (bản prerender, điện thoại, Linux) ⇒ cả hai cùng nền đặc, không nhãn.
 *
 * Tone mặc định (nền trắng): `#faq` ngay trên là nền mờ, `#cta` ngay dưới là gradient.
 */
export function DownloadSection() {
  const copy = useCopy();
  const d = copy.download;
  const detected = useDesktopPlatform();

  return (
    <Section id="download" labelledBy="download-head" cv className="nx-cv-download">
      <Container>
        <div className="grid items-center gap-12 lg:grid-cols-2 xl:gap-16">
          <Reveal>
            <Eyebrow>{d.eyebrow}</Eyebrow>
            <Heading level={2} id="download-head" className="mt-[14px]">
              {d.heading}
            </Heading>
            <Text size="lead" className="mt-4 max-w-[54ch]">
              {d.lead}
            </Text>

            <ul className="mt-7 flex flex-col gap-3">
              {d.highlights.map((item) => (
                <li key={item.text} className="flex items-center gap-3 text-body-sm text-ink-700">
                  <span className="grid size-8 flex-none place-items-center rounded-tile bg-brand-50 text-brand-600">
                    <Icon name={item.icon} size={16} />
                  </span>
                  {item.text}
                </li>
              ))}
            </ul>

            <div className="mt-9 grid gap-5 sm:grid-cols-2 sm:gap-4">
              {PLATFORMS.map((platform) => {
                const p = d.platforms[platform];
                const mine = detected === platform;
                return (
                  <div key={platform}>
                    <DownloadButton
                      platform={platform}
                      href={DESKTOP_DOWNLOADS[platform].url}
                      overline={p.overline}
                      name={p.name}
                      emphasis={detected === null || mine ? 'primary' : 'secondary'}
                      {...(mine ? { badge: d.recommended } : {})}
                    />
                    <p className="mt-2.5 px-1 text-caption text-ink-500">
                      {p.requirement} · {p.fileType}
                    </p>
                  </div>
                );
              })}
            </div>

            <p className="mt-5 px-1 text-caption text-ink-500">{d.intelNote}</p>
            <p className="mt-1.5 px-1 text-body-sm text-ink-600">
              {d.webPrompt}{' '}
              <a
                href={APP_URL}
                className="font-semibold text-brand-600 underline-offset-4 transition [transition-duration:var(--duration-hover)] hover:text-brand-900 hover:underline"
              >
                {d.webLink}
              </a>
            </p>

            {/*
              `<details>` native như `FaqItem`: chạy không cần JS, screen reader đọc trạng thái miễn
              phí. Gập mặc định — checksum là thông tin cho người CẦN, không phải cho mọi người.
              Trang TRỎ tới tệp `.sha256` chứ không in hash: xem docblock `DESKTOP_DOWNLOADS`.
            */}
            <details className="nx-disclose mt-6 border-t border-ink-100 pt-4">
              <summary className="flex min-h-11 cursor-pointer list-none items-center gap-2 px-1 text-caption font-semibold text-ink-700 md:min-h-0 [&::-webkit-details-marker]:hidden">
                <Icon name="shield-check" size={15} className="text-brand-600" />
                <span>{d.checksumToggle}</span>
                <Icon name="plus" size={14} className="nx-disclose-mark text-ink-400" />
              </summary>
              <div className="mt-3 flex flex-col gap-3 px-1">
                <Text size="caption" tone="muted">
                  {d.checksumHint}
                </Text>
                {PLATFORMS.map((platform) => (
                  <div key={platform}>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-micro font-bold tracking-wide text-ink-500 uppercase">
                        {d.platforms[platform].name}
                      </span>
                      <a
                        href={DESKTOP_DOWNLOADS[platform].sha256Url}
                        target="_blank"
                        rel="noopener"
                        className="inline-flex min-h-11 items-center text-caption font-semibold text-brand-600 underline-offset-4 transition [transition-duration:var(--duration-hover)] hover:text-brand-900 hover:underline md:min-h-0"
                      >
                        {d.checksumLink}
                      </a>
                    </div>
                    <code className="mt-1 block rounded-field bg-surface-muted px-3 py-2 font-mono text-micro break-all text-ink-700 select-all">
                      {d.platforms[platform].verifyCommand}
                    </code>
                  </div>
                ))}
              </div>
            </details>
          </Reveal>

          <Reveal className="nx-rv-right">
            <DesktopMock />
          </Reveal>
        </div>
      </Container>
    </Section>
  );
}
